/**
 * Suggested-question chips — which four prompts to offer on a given route, for
 * a given persona.
 *
 * Port of `renderSugg(v)` (demo line 4096) and the thirteen extra view keys
 * `Object.assign(SUGG, { … })` added at demo line 4094.
 *
 * ## What lives where
 *
 * | Piece | Home |
 * |---|---|
 * | `SUGG` — sixteen base view keys (demo 4092) | `../seed/suggestions.ts` (P1) |
 * | `PSUGG` — six persona keys (demo 4093) | `../seed/suggestions.ts` (P1) |
 * | The thirteen extra view keys (demo 4094), plus `priorities` / `routing` | {@link SUGGESTIONS_EXTRA}, here |
 * | Machine Operators / Reliability tab chips | {@link MACHINE_TAB_SUGGESTIONS}, here |
 * | Route → view key | {@link viewForPath}, here |
 * | The pick-four rule | {@link suggestionsFor}, here |
 *
 * The seed is data; this module is the behaviour the seed's own header says it
 * deliberately left out. The extra thirteen keys land here rather than in the
 * seed because the seed's `SuggestionView` union is a closed set P1 owns; this
 * module widens it without editing it.
 *
 * ## The pick-four rule, verbatim
 *
 * 1. Start from `SUGG[view]`, falling back to `SUGG.fleet`.
 * 2. On `overview`, `fleet` and `approvals` only, prepend the persona's own
 *    prompts from `PSUGG`.
 * 2b. fork(P7a/C3): prepend {@link LEAD_SUGGESTIONS} for the view, and drop
 *    any later repeat of a prompt already taken.
 * 3. Take the first four.
 * 4. On a machine page, rewrite the three deictic prompts against the machine
 *    in view: `'this machine'` → the id, `'on it'` → `'on <id>'`,
 *    `'its telemetry'` → `"<id>'s telemetry"`.
 *
 * `reportSugg()` (demo line 4095) is ported as {@link reportSuggestions}. The
 * demo branched on the report currently open in the main pane; here that is
 * the report the route addresses (`/reports/email/:mine`, `/reports/deck/:mine`,
 * `/reports/weekly/:division`). With no report open — the index, the S.O.S
 * page — the `reports` view falls back to the seed's three static prompts, as
 * the demo did with no `REPORT_CUR`.
 *
 * Engine layer (layer 2 of the 4-layer architecture) — pure, no React.
 *
 * @see src/versions/v1/shared/seed/suggestions.ts — the ported prompt data
 * @see src/versions/v1/chrome-oem/AskStrip.tsx — the chip row that renders these
 */

import { IDS, MINES, MINE_ORDER } from '../../data/core.ts';
import type { MineCode } from '../../schema/machine.ts';
import { reportRefFromPath } from '../report-catalog.ts';
import type { OemPersonaId } from '../../schema/persona.ts';
import {
  PERSONA_SUGGESTIONS,
  SUGGESTIONS,
  sg,
  sgm,
  suggestionText,
  type SuggestionPrompt,
} from '../seed/suggestions.ts';

/** The thirteen view keys the demo bolted on after declaring `SUGG`, plus the fork's `priorities` and `routing` (oem v1.8). */
export const EXTRA_SUGGESTION_VIEWS = [
  'asset360',
  'module',
  'loops',
  'issues',
  'jobboard',
  'remote',
  'surface',
  'sos',
  'techs',
  'customers',
  'jobcards',
  'partsflow',
  'population',
  // fork(oem v1.8): Paul Telford's feedback — the driver tree and the routing board.
  'priorities',
  'routing',
] as const;

/** A view key this module can answer for — the seed's sixteen plus thirteen. */
export type AssistantView =
  | keyof typeof SUGGESTIONS
  | (typeof EXTRA_SUGGESTION_VIEWS)[number];

/**
 * Demo line 4094 — `Object.assign(SUGG, { … })`.
 *
 * Same segment encoding as the seed: literal text verbatim (note the ASCII
 * apostrophe in "today's"), machine ids as `{machineAlias}` placeholders.
 */
export const SUGGESTIONS_EXTRA: Readonly<
  Record<(typeof EXTRA_SUGGESTION_VIEWS)[number], readonly SuggestionPrompt[]>
> = {
  asset360: [
    [sg('What is in each Asset360 module this morning?')],
    [sg('What does doing nothing cost us today?')],
    [sg('Which jobs are waiting on a person?')],
  ],
  module: [
    [sg('What is waiting in this module?')],
    [sg('Which agents run this module and what did they do overnight?')],
  ],
  loops: [
    [sg('Which fleet rules came out of the incidents?')],
    [sg('Which contracts are up for renewal?')],
  ],
  issues: [
    [sg('What does doing nothing cost us today?')],
    [sg('How confident should I be in the '), sgm('J07'), sg(' prediction?')],
    [sg('What needs a decision today?')],
  ],
  jobboard: [
    [sg('Which jobs are waiting on a person?')],
    [sg('What is the most expensive open job?')],
  ],
  remote: [
    [sg('How much travel has remote support avoided?')],
    [sg('Which sessions became knowledge articles?')],
  ],
  surface: [
    [sg('Which surface drills are at risk this week?')],
    [sg('What did the S-05 string incident cost versus a planned pipe change?')],
    [sg('Generate the weekly status report for Surface Drilling')],
  ],
  sos: [
    [sg("Generate today's S.O.S report for Gawler Deep")],
    [sg('What needs a decision today?')],
  ],
  techs: [
    [sg('Which technician should take the S-09 rotary head job and why?')],
    [sg('Whose certificates expire in the next 45 days?')],
  ],
  customers: [
    [sg('How did customers rate us this month?')],
    [sg('Write the machine risk email for Ochre Hill')],
  ],
  jobcards: [
    [sg('What are the likely causes of the L-20 turbo failure?')],
    [sg('What will the S-04 hammer exchange cost?')],
  ],
  partsflow: [
    [sg('Which parts do we need in the next 30 days that are not on the shelf?')],
    [sg('Is the rotary head motor for S-09 the only one in the network?')],
  ],
  population: [
    [sg('Which machines are hot spots right now?')],
    [sg('List the bridging units')],
  ],
  // fork(oem v1.8) — `/priorities`, the driver tree.
  priorities: [
    [sg('Which machine should we target first?')],
    [sg('What is driving the exposure at Mount Isolde?')],
    [sg('Who owns the top three?')],
  ],
  // fork(oem v1.8) — `/issues/routing`, operator vs equipment routing.
  routing: [
    [sg('Which operators need training?')],
    [sg('Which observations go to Mine Control?')],
    [sg('Who should the '), sgm('T22'), sg(' brake alert go to?')],
  ],
};

/**
 * fork(oem v1.8): the machine page's chips on its Operators and Reliability
 * tabs (`?tab=operators`, `?tab=reliability`). They lead the machine view's
 * row there, ahead of {@link LEAD_SUGGESTIONS}, and take the same deictic
 * rewrite ("this machine" → the id, "on it" → "on <id>").
 */
export const MACHINE_TAB_SUGGESTIONS: Readonly<Record<'operators' | 'reliability', readonly SuggestionPrompt[]>> = {
  operators: [
    [sg('Who was driving this machine when it exceeded?')],
    [sg('Is this an operator or equipment problem on it?')],
    [sg('Is this machine in control?')],
  ],
  reliability: [
    [sg('Is this machine in control?')],
    [sg("What's the TARP level on it?")],
    [sg('Who was driving this machine when it exceeded?')],
  ],
};

/**
 * Prompts pushed to the front of a view's chip row — fork(P7a/C3).
 *
 * Step 6 of the six-click story in `docs/plans/simplification-pass.md` §C is
 * the assistant answering "what did the L-02 failure cost versus a planned
 * change?" with A$11,350, 3.9× the cost of doing it on schedule, the rows it
 * read, and a link to the incident. It was a sentence the presenter had to
 * type. Now it is the first chip on the two pages the story arrives at.
 *
 * The segments are the seed's own — `sgm('L14')` resolves through `IDS`, so
 * the id here cannot drift from the id the `alerts` and `incident` views
 * already ask about. {@link suggestionsFor} de-duplicates by rendered text,
 * so listing a view that already carries the prompt only moves it to the
 * front rather than showing it twice.
 *
 * On a machine page the question keeps naming L-02 on every machine, not the
 * machine in view: it is a specific failure with a specific number behind it,
 * and the deictic rewrite in {@link personaliseForMachine} deliberately has
 * nothing to bite on here.
 */
export const LEAD_SUGGESTIONS: Readonly<
  Partial<Record<AssistantView, readonly SuggestionPrompt[]>>
> = {
  costs: [[sg('What did the '), sgm('L14'), sg(' failure cost versus a planned change?')]],
  machine: [[sg('What did the '), sgm('L14'), sg(' failure cost versus a planned change?')]],
  // fork(oem v1.8): Paul Telford's routing question, first on an alert.
  alerts: [[sg('Is this an operator or equipment problem?')], [sg('Who should this go to?')]],
};

/** Every view key, seed plus extras. */
const ALL_SUGGESTIONS: Readonly<Record<string, readonly SuggestionPrompt[]>> = {
  ...SUGGESTIONS,
  ...SUGGESTIONS_EXTRA,
};

/** Demo line 4096. The three views that also get the persona's own prompts. */
export const PERSONA_SUGGESTION_VIEWS = ['overview', 'fleet', 'approvals'] as const;

/** Demo line 4096. `base.slice(0, 4)`. */
export const SUGGESTION_LIMIT = 4;

// ---------------------------------------------------------------------------
// Route → view key
// ---------------------------------------------------------------------------

/**
 * Exact-match routes. The inverse of `oem-routes.ts`'s `SIMPLE_VIEW`, with the
 * demo's view key on the right.
 */
const EXACT_VIEW: Readonly<Record<string, AssistantView>> = {
  '/': 'asset360',
  '/issues': 'issues',
  '/fleet': 'fleet',
  '/sites': 'overview',
  '/costs': 'costs',
  '/approvals': 'approvals',
  '/workflows': 'workflows',
  '/loops': 'loops',
  '/assets': 'fleet',
  '/customers': 'customers',
  '/job-board': 'jobboard',
  '/job-cards': 'jobcards',
  '/parts-flow': 'partsflow',
  '/population': 'population',
  '/technicians': 'techs',
  '/remote-support': 'remote',
  '/reports': 'reports',
  '/reports/sos': 'sos',
  '/integration': 'data',
  '/integration/data': 'data',
  '/connectors': 'connectors',
  '/agents': 'agents',
  '/priorities': 'priorities',
  '/issues/routing': 'routing',
};

/** Prefix routes, longest first — a nested route wins over its parent. */
const PREFIX_VIEW: readonly (readonly [string, AssistantView])[] = [
  // fork(L3): a generated report — `reportSuggestions` picks by the report.
  ['/reports/email', 'reports'],
  ['/reports/deck', 'reports'],
  ['/reports/weekly', 'reports'],
  ['/issues/alerts', 'alerts'],
  ['/issues/incidents', 'incident'],
  ['/workflows/modules', 'module'],
  ['/workflows/steps', 'workflows'],
  ['/workflows/case-trace', 'workflows'],
  ['/assets/drilling', 'drilling'],
  ['/assets/loadhaul', 'loadhaul'],
  ['/assets/surface', 'surface'],
  ['/machines', 'machine'],
  ['/sites', 'overview'],
  ['/loops', 'loops'],
];

/** The demo's own fallback — `SUGG[v] || SUGG.fleet`. */
export const DEFAULT_VIEW: AssistantView = 'fleet';

/** Route → the demo view key whose suggestions belong on it. */
export function viewForPath(pathname: string): AssistantView {
  const path = pathname.replace(/\/+$/, '') || '/';
  const exact = EXACT_VIEW[path];
  if (exact) return exact;
  const hit = PREFIX_VIEW.find(([p]) => path === p || path.startsWith(`${p}/`));
  return hit ? hit[1] : DEFAULT_VIEW;
}

/** `/machines/L-02` → `'L-02'`; anything else → `null`. */
export function machineFromPath(pathname: string): string | null {
  const m = /^\/machines\/([^/?#]+)/.exec(pathname);
  return m ? decodeURIComponent(m[1]).toUpperCase() : null;
}

// ---------------------------------------------------------------------------
// The pick-four rule
// ---------------------------------------------------------------------------

/**
 * Demo line 4096 — the machine-page rewrite, applied only on that view.
 *
 * The three replacements are the demo's, in its order, and each fires at most
 * once because `String.replace` with a string pattern is not global.
 */
export function personaliseForMachine(prompt: string, machineId: string): string {
  return prompt
    .replace('this machine', machineId)
    .replace('on it', `on ${machineId}`)
    .replace('its telemetry', `${machineId}'s telemetry`);
}

/** What `suggestionsFor` needs beyond the route. */
export interface SuggestionOptions {
  /** Overrides the id parsed from the path — used by the machine scene. */
  readonly machineId?: string | null;
  /** Overrides the view key parsed from the path — used by `<AskStrip view=…>`. */
  readonly view?: AssistantView;
  /**
   * fork(oem v1.8): the machine page's open tab (`?tab=`). On `operators` and
   * `reliability` the {@link MACHINE_TAB_SUGGESTIONS} lead the row.
   */
  readonly tab?: string | null;
}

/**
 * Demo line 4096. `renderSugg(v)`, as data: the four prompts to show.
 *
 * Deterministic — same route, same persona, same four strings, same order.
 */
export function suggestionsFor(
  pathname: string,
  personaId: OemPersonaId,
  options: SuggestionOptions = {},
): string[] {
  const view = options.view ?? viewForPath(pathname);
  const fromReport = view === 'reports' ? reportSuggestions(pathname) : null;
  const base = fromReport
    ? fromReport.map((text) => [sg(text)] as SuggestionPrompt)
    : [...(ALL_SUGGESTIONS[view] ?? ALL_SUGGESTIONS[DEFAULT_VIEW])];

  if ((PERSONA_SUGGESTION_VIEWS as readonly string[]).includes(view)) {
    base.unshift(...(PERSONA_SUGGESTIONS[personaId] ?? []));
  }

  // fork(P7a/C3). Ahead of the persona prompts too — the two sets never meet
  // today (no view is in both tables), and if one ever is, the story chip is
  // the one the presenter is reaching for.
  base.unshift(...(LEAD_SUGGESTIONS[view] ?? []));

  // fork(oem v1.8): the machine page's Operators / Reliability tab chips lead.
  const tabChips =
    view === 'machine' && (options.tab === 'operators' || options.tab === 'reliability')
      ? MACHINE_TAB_SUGGESTIONS[options.tab]
      : [];
  base.unshift(...tabChips);

  const machineId = options.machineId ?? machineFromPath(pathname);
  const chips: string[] = [];
  const seen = new Set<string>();
  for (const prompt of base) {
    const text = suggestionText(prompt, IDS);
    // De-duplicate on the rendered string, not the segments: a lead prompt
    // and a seed prompt can be the same question written two ways.
    if (seen.has(text)) continue;
    seen.add(text);
    chips.push(
      view === 'machine' && machineId ? personaliseForMachine(text, machineId) : text,
    );
    if (chips.length === SUGGESTION_LIMIT) break;
  }
  return chips;
}

/**
 * Demo line 4095. `reportSugg()` — three follow-ups for the report open on
 * this route: the same report for the next mine (or the other division), the
 * other kind for this mine, and a question about the report itself. `null`
 * when the route addresses no generated report.
 */
export function reportSuggestions(pathname: string): string[] | null {
  const r = reportRefFromPath(pathname);
  if (!r || r.kind === 'sos') return null;
  const other = (m: MineCode): MineCode => MINE_ORDER.filter((x) => x !== m)[0];
  if (r.kind === 'email') {
    return [
      `Write the machine risk email for ${MINES[other(r.mine)].name}`,
      `Create the ${MINES[r.mine].name} equipment summary presentation`,
      'Which machine in this report should I look at first?',
    ];
  }
  if (r.kind === 'deck') {
    return [
      `Write the machine risk email for ${MINES[r.mine].name}`,
      `Create the ${MINES[other(r.mine)].name} equipment summary presentation`,
      `Why is ${MINES[r.mine].name}'s cost per operating hour where it is?`,
    ];
  }
  return [
    `Generate the weekly status report for ${r.kind === 'drills' ? 'Load & Haul' : 'Underground Drilling'}`,
    'Write the machine risk email for Mount Isolde',
    'Which machine in this report should I look at first?',
  ];
}
